import tinycolor from 'tinycolor2';
import {
    TextAlignType,
    TextVerticalAlign,
    TextWritingType,
    type ILayerProps,
    type ISize,
    type IVectorLayerProps,
    type TextContentPadding,
    type VectorTextInfo,
} from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';

/** 默认 Vector Text 候选在浅色或无法判断的图形上使用的深色文字。 */
const DARK_TEXT_COLOR = '#000000ff';

/** 默认 Vector Text 候选在深色纯色图形上使用的浅色文字。 */
const LIGHT_TEXT_COLOR = '#ffffffff';

export type VectorTextPaddingLimits = {
    horizontal: number;
    vertical: number;
};

/**
 * 计算对称 Vector Text padding 的轴向上限。
 *
 * 空文字编辑仍需要容纳一个最小行框：横排在垂直轴预留行高，竖排在水平轴预留行宽；
 * 行内轴至少保留 1px，避免 padding 把内容框完全吃掉。
 *
 * @param layerSize - Vector 主动尺寸。
 * @param textInfo - 当前文字样式。
 * @returns 横向与纵向单边 padding 上限。
 */
export const resolveVectorTextPaddingLimits = (layerSize: ISize, textInfo: VectorTextInfo): VectorTextPaddingLimits => {
    const lineSize = Math.max(Math.ceil(textInfo.fontSize * (textInfo.lineSpacing || 1.2)), 1);
    const isHorizontal = textInfo.writingMode === TextWritingType.HORIZONTAL_TB;

    return {
        horizontal: Math.max(Math.floor((layerSize.width - (isHorizontal ? 1 : lineSize)) / 2), 0),
        vertical: Math.max(Math.floor((layerSize.height - (isHorizontal ? lineSize : 1)) / 2), 0),
    };
};

/**
 * 把 Vector Text padding 收敛到当前主动尺寸可容纳的范围。
 *
 * @param layerSize - Vector 主动尺寸。
 * @param textInfo - 当前文字样式。
 * @param padding - 待归一化 padding；默认使用 textInfo.padding。
 * @returns 四边归一化后的 padding。
 */
export const normalizeVectorTextPadding = (
    layerSize: ISize,
    textInfo: VectorTextInfo,
    padding: TextContentPadding = textInfo.padding ?? { top: 0, right: 0, bottom: 0, left: 0 },
): TextContentPadding => {
    const limits = resolveVectorTextPaddingLimits(layerSize, textInfo);

    return {
        top: Math.max(0, Math.min(padding.top, limits.vertical)),
        right: Math.max(0, Math.min(padding.right, limits.horizontal)),
        bottom: Math.max(0, Math.min(padding.bottom, limits.vertical)),
        left: Math.max(0, Math.min(padding.left, limits.horizontal)),
    };
};

/**
 * 创建矢量图层默认文本信息
 * @param layer - 矢量图层
 * @param override - 覆盖默认值
 * @returns - 文本信息
 */
export const createDefaultVectorLayerTextInfo = (layerSize: ISize, override: Partial<VectorTextInfo> = {}) => {
    const { width, height } = layerSize;
    const defaultFontSize = Math.round(width / 10);

    const ret: VectorTextInfo = {
        text: '',
        fontFamily: DEFAULT_FONT_FAMILY().name,
        fontSize: defaultFontSize >= height ? height * 0.6 : defaultFontSize,
        color: '#000000ff',
        bold: false,
        italic: false,
        lineSpacing: 1.4,
        letterSpacing: 0,
        lineThrough: false,
        underline: false,
        textAlign: TextAlignType.CENTER,
        writingMode: TextWritingType.HORIZONTAL_TB,
        listStyle: '',
        opacity: 1,
        verticalAlign: TextVerticalAlign.CENTER,
        padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        },
    };

    return {
        ...ret,
        ...override,
    };
};

/**
 * 根据图形纯色填充的明暗选择默认 Vector Text 候选颜色。
 *
 * 渐变、复杂图形配色、半透明填充和无效颜色都无法可靠判断明暗，统一使用黑字，
 * 不为了取色而新增图形采样。
 *
 * @param props - 当前 Vector Layer 数据。
 * @returns 深色文字或浅色文字的持久化颜色值。
 */
const resolveDefaultVectorTextColor = (props: IVectorLayerProps): string => {
    if (props.fillGradient || !props.fill) return DARK_TEXT_COLOR;

    const color = tinycolor(props.fill);
    if (!color.isValid() || color.getAlpha() < 0.5) return DARK_TEXT_COLOR;

    return color.isDark() ? LIGHT_TEXT_COLOR : DARK_TEXT_COLOR;
};

/**
 * 为进入文字编辑态的 Vector 创建同 ID、同 layer type 的候选工作值。
 *
 * 候选只存在于 Text Editor session 中；是否写入 flatLayer 由编辑规则决定。
 *
 * @param layer - 请求进入文字编辑态的真实 Vector Layer。
 * @returns 带有 textInfo 的 Vector 工作值；原本已有 textInfo 时原样返回入参。
 */
export const createVectorTextEditingCandidate = (layer: ILayerProps): ILayerProps => {
    const data = layer.data as IVectorLayerProps;
    if (data.textInfo) return layer;

    return {
        ...layer,
        data: {
            ...data,
            textInfo: createDefaultVectorLayerTextInfo(
                { width: layer.width, height: layer.height },
                { color: resolveDefaultVectorTextColor(data) },
            ),
        },
    };
};
